<template>
	<ul
		v-if="passwordValidation && passwordValidation.active"
		class="password-validator"
		data-cy="password-validator"
	>
		<li
			v-for="criterion in criteria"
			:key="criterion.translationKey"
			class="password-validator__criterion"
		>
			<span
				:class="[
					'password-validator__criterion-oval',
					{ 'password-validator__criterion-oval--success': criterion.successMethod() }
				]"
				data-cy="password-validator-criterion"
			>
				<helio-icon
					v-if="canDisplayCheckedIcon && criterion.successMethod()"
					custom-classes="password-validator__icon password-validator__icon--checked"
					:token="checkedIcon.token"
					:weight="checkedIcon.weight ? checkedIcon.weight : 'regular'"
				/>

				<helio-icon
					v-else-if="canDisplayUncheckedIcon && !criterion.successMethod()"
					custom-classes="password-validator__icon password-validator__icon--unchecked"
					:token="uncheckedIcon.token"
					:weight="uncheckedIcon.weight ? uncheckedIcon.weight : 'regular'"
				/>
			</span>

			<span class="password-validator__criterion-text">
				{{ $t(criterion.translationKey) }}
			</span>
		</li>
	</ul>
</template>

<script>
export default {
	name: 'PasswordValidator',

	props: {
		password: {
			type: String,
			required: true,
			description: 'This is the value of the password field',
		},

		passwordValidation: {
			type: Object,
			required: true,
			default: null,
			description: 'Shows a visual indication of the password\'s validation',
		},
	},

	data() {
		return {
			criteria: [
				{
					successMethod: this.hasCompleteLength,
					translationKey: 'password-validator.criteria.min-chars',
				},
				{
					successMethod: this.hasAtLeastOneNumber,
					translationKey: 'password-validator.criteria.number',
				},
				{
					successMethod: this.hasAtLeastOneLowerCase,
					translationKey: 'password-validator.criteria.lowercase',
				},
				{
					successMethod: this.hasAtLeastOneSpecialCaracter,
					translationKey: 'password-validator.criteria.special-char',
				},
				{
					successMethod: this.hasAtLeastOneUpperCase,
					translationKey: 'password-validator.criteria.uppercase',
				},
			],
		};
	},

	computed: {
		canDisplayCheckedIcon() {
			return this.passwordValidation.icons
				&& this.checkedIcon
				&& this.checkedIcon.token;
		},

		canDisplayUncheckedIcon() {
			return this.passwordValidation.icons
				&& this.uncheckedIcon
				&& this.uncheckedIcon.token;
		},

		checkedIcon() {
			return this.passwordValidation.icons.checked;
		},

		uncheckedIcon() {
			return this.passwordValidation.icons.unchecked;
		},
	},

	watch: {
		password() {
			this.$emit('password-validation', this.isAllRulesRespected());
		},
	},

	methods: {
		hasCompleteLength() {
			return this.password.length >= 8;
		},

		hasAtLeastOneNumber() {
			return /\d/.test(this.password);
		},

		hasAtLeastOneLowerCase() {
			return /[a-z]/.test(this.password);
		},

		hasAtLeastOneUpperCase() {
			return /[A-Z]/.test(this.password);
		},

		hasAtLeastOneSpecialCaracter() {
			const format = /[$*.^[\]{}()?\-"!@#%&/\\,><':;|_~`?]+/;
			return format.test(this.password);
		},

		isAllRulesRespected() {
			return this.hasCompleteLength() && this.hasAtLeastOneNumber()
				&& this.hasAtLeastOneLowerCase() && this.hasAtLeastOneUpperCase()
				&& this.hasAtLeastOneSpecialCaracter();
		},
	},
};
</script>

<style scoped lang="scss" src="./password-validator.scss" />
